
import React, { useState } from 'react';
import { ServiceItem, ServiceTransaction } from '../types';
import { Button } from './Button';
import { ClampedText } from './ClampedText';
import { agreedMinHours, initialBookingAmountText, isHourlyUnit, readMinHoursInput } from '../services/serviceUnits';
import { bestandteilStandKurz, transferOriginLabel } from '../services/projectLv';
import { isNepReasonGiven, isNepService, nepReasonLabel, NEP_BADGE, NEP_HINT } from '../services/nepPosition';
import { PerformerOption } from '../services/performerChoice';
import { bookingProgress } from './project-hub/serviceDetailLogic';
import { bookingIsFlatProof, FLAT_PROOF_HINT } from '../services/flatRateLogic';
import { isFlatProofEntry } from '../services/serviceLogUtils';
import { hatBestandteile } from '../services/bestandteile/bestandteilLogic';
import { X, CheckCircle2, History, FileText, Maximize2, AlertTriangle, Info, UserRound, ListChecks } from 'lucide-react';

interface Props {
    isOpen: boolean;
    onClose: () => void;
    service: ServiceItem;
    logs: ServiceTransaction[];
    /**
     * `performerId` ist die Auswahl aus „Erbracht durch" — nur gesetzt, wenn
     * der Dialog die Auswahl zeigt (Admin). Sonst undefined, und der Aufrufer
     * bucht wie bisher auf den angemeldeten Nutzer.
     *
     * `nepReason` ist die Pflicht-Begruendung einer Buchung auf eine
     * NEP-Position — nur dann gesetzt, sonst undefined. Eigenes Argument, weil
     * sie als eigenes Feld auf die Buchung gehoert (ServiceTransaction.nepReason)
     * und nie in die Beschreibung eingemischt wird.
     */
    onSave: (amount: number, description: string, date: string, performerId?: string, nepReason?: string) => void;
    /**
     * FUER WEN gebucht wird. Leer oder fehlend = kein Feld (Nicht-Admin).
     * Die Buchhaltung traegt Begehungen fuer Koordinatoren nach — ohne dieses
     * Feld stand ihr eigener Name im Protokoll und im Leistungsnachweis.
     */
    performerOptions?: PerformerOption[];
    defaultPerformerId?: string;
    /**
     * Gesetzt, wenn die gewaehlte Position (etwa 2.7.10) von der
     * Begehungspauschale gedeckt ist: gebucht wird dann der Nachweis auf der
     * Pauschale (useReportActions.planTrackServiceBooking lenkt um). Titel der
     * Pauschale, damit der Dialog sagt, wohin die Buchung geht.
     */
    flatRedirectTitle?: string;
    /**
     * PAKETPOSITION (Position mit Bestandteilen): der Dialog bucht sie nie
     * direkt — Pakete bucht nur der Bestandteile-Weg (Kernregel 7). Statt des
     * Formulars steht ein Hinweis da; mit diesem Rueckruf dazu der Knopf, der
     * den Bestandteile-Dialog oeffnet.
     */
    onOpenBestandteile?: () => void;
}

// Deutsche Anzeige der Mindeststunden (2 -> "2", 1.5 -> "1,5") — wortgleich zum
// Abrechnungsdialog, damit dieselbe Vorgabe ueberall gleich dasteht.
const formatHoursLabel = (value: number): string => value.toLocaleString('de-DE');
const formatQuantity = (value: number): string => value.toLocaleString('de-DE', { maximumFractionDigits: 2 });

export const ServiceTrackingModal: React.FC<Props> = ({ isOpen, onClose, service, logs, onSave, performerOptions = [], defaultPerformerId = '', flatRedirectTitle, onOpenBestandteile }) => {
    const isHourly = isHourlyUnit(service.unit);
    // Paketposition: kein Buchungsziel dieses Dialogs (siehe onOpenBestandteile).
    const istPaket = hatBestandteile(service);
    const showPerformer = performerOptions.length > 0;
    // NEP (Eventualposition): nicht beauftragt. Eine Buchung darauf ist
    // moeglich — der Kunde beauftragt so etwas oft nachtraeglich per Zuruf —
    // aber nur mit Begruendung, die im Protokolltext mitgeht (nepPosition).
    const isNep = isNepService(service);
    // Pauschale: jede Buchung ist eine Nachweiszeile (Menge 0, NW-…), der
    // Monat zaehlt allein ueber FLAT-JJJJ-MM. Ein Mengenfeld haette hier
    // nichts zu sagen — und verlangte bisher eine Menge > 0, die dann
    // quantityUsed der Pauschale anhob (ein Monat Geld ohne Schluessel).
    // Dasselbe gilt fuer eine von der Pauschale GEDECKTE Begehungsposition:
    // gebucht wird der Nachweis auf der Pauschale (flatRedirectTitle).
    const isFlatProof = bookingIsFlatProof(service) || !!flatRedirectTitle;
    /*
     * BEWUSSTE Auswahl getrennt vom Default: die Nutzerliste kommt asynchron
     * (useProjectHubData.fetchUsers). Wird der Dialog davor geoeffnet, steht
     * zunaechst nur der Admin drin; ein einmalig per useState uebernommener
     * Default bliebe dann am Admin haengen, obwohl der Koordinator inzwischen
     * geladen ist. Deshalb: null = nichts gewaehlt, es gilt der jeweils
     * aktuelle Default. Erst ein Klick im Select friert die Wahl ein — und
     * auch die nur, solange sie noch in der Liste steht.
     *
     * Fällt die BEWUSST gewählte Person aus der Liste (gesperrt, Nr. 148, die
     * Sperrliste kam erst nach dem Klick), springt die Auswahl NICHT still auf
     * den Default — sonst buchte das Speichern eine andere Person. Dann muss
     * neu gewählt werden.
     */
    const [chosenPerformerId, setChosenPerformerId] = useState<string | null>(null);
    const auswahlEntfallen = showPerformer && chosenPerformerId !== null && !performerOptions.some(o => o.id === chosenPerformerId);
    const performerId = chosenPerformerId !== null && !auswahlEntfallen
        ? chosenPerformerId
        : auswahlEntfallen ? '' : defaultPerformerId;
    // Vereinbarte Mindeststunden dieser Position, sonst null. Nur wo etwas
    // vereinbart ist, wird vorbelegt, gewarnt und der Hinweis gezeigt.
    const minHours = agreedMinHours(service);
    /*
     * ROHTEXT statt Zahl im State — dieselbe Regel wie im Abrechnungsdialog:
     * das Feld ist type="text", weil ein Zahlenfeld die Zwischenstufe "2."
     * als leeren Wert liefert und damit die halb getippte Eingabe loescht.
     * Der Text darf deshalb nie aus dem Zahlenwert zurueckgespeist werden.
     */
    const [amountText, setAmountText] = useState<string>(() => initialBookingAmountText(service));
    const [description, setDescription] = useState('');
    const [nepReason, setNepReason] = useState('');
    const [showLargeInput, setShowLargeInput] = useState(false);
    
    const getLocalDateStr = () => {
        const d = new Date();
        const offset = d.getTimezoneOffset() * 60000;
        return new Date(d.getTime() - offset).toISOString().split('T')[0];
    };

    const [customDate, setCustomDate] = useState(getLocalDateStr());
    const [customTime, setCustomTime] = useState(new Date().toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' }));

    // Eine Zahl liegt nur bei lesbarer Eingabe vor. Ein Trenner am Ende ("2." /
    // "2,") zaehlt als die sichtbare 2; unlesbares wie "abc" ergibt 0 und sperrt
    // das Speichern, loescht aber nie den bereits getippten Text.
    const parsedAmount = readMinHoursInput(amountText);
    // Die Pauschale bucht immer 0 — was im (unsichtbaren) Feld steht, zaehlt nicht.
    const amount = isFlatProof ? 0 : (parsedAmount.kind === 'value' ? parsedAmount.value : 0);
    const isValid = isFlatProof || amount > 0;
    // Unterschreiten der Vorgabe ist ausdruecklich erlaubt — nur eine Warnung,
    // nie eine Sperre.
    const belowMin = !isFlatProof && minHours !== null && amount > 0 && amount < minHours;
    // Getrennt von isValid: das Mengenfeld soll nicht rot werden, nur weil
    // die Begruendung noch fehlt — der Fehler steht am Begruendungsfeld.
    const nepReasonMissing = isNep && !isNepReasonGiven(nepReason);
    const canSave = isValid && !nepReasonMissing && !auswahlEntfallen && !istPaket;
    const progress = bookingProgress(service, amount);

    if (!isOpen) return null;

    const handleSubmit = () => {
        if (!canSave) return;

        let isoDate = new Date().toISOString();
        try {
            const d = new Date(`${customDate}T${customTime}`);
            if (!isNaN(d.getTime())) {
                isoDate = d.toISOString();
            }
        } catch {
            console.error("Invalid date format, using current time");
        }

        onSave(
            amount,
            description,
            isoDate,
            showPerformer ? performerId : undefined,
            isNep ? nepReason.trim() : undefined,
        );
        onClose();
        setAmountText(initialBookingAmountText(service));
        setDescription('');
        setNepReason('');
    };

    const formatDate = (d: string) => new Date(d).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
    const sortedLogs = [...logs].sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());

    return (
        <div className="fixed inset-0 z-[200] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 animate-fade-in">
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md flex flex-col max-h-[90vh] overflow-hidden animate-scale-in relative border border-slate-200">
                
                <div className="bg-slate-900 text-white p-5 flex justify-between items-start shrink-0">
                    <div>
                        <ClampedText as="h3" text={service.title} lines={3} className="font-bold text-lg leading-tight break-words" />
                        <p className="text-slate-400 text-xs mt-1 font-mono flex items-center gap-2">
                            {service.lvPosition}
                            {isNep && <span className="font-sans text-[10px] font-black text-amber-950 bg-amber-300 px-1.5 rounded" title={NEP_HINT}>{NEP_BADGE}</span>}
                        </p>
                        {isNep && <p className="text-amber-300 text-[11px] mt-1">{NEP_HINT}</p>}
                        {/* HERKUNFT: an einer Mutter mit mehreren Toechtern gibt es
                            mehrere Zeilen mit derselben LV-Nummer und demselben Titel.
                            Ohne diese Zeile ist im Buchungsmoment nicht zu erkennen,
                            auf welche gebucht wird — und eine Buchung auf der Position
                            der Schwester faellt erst bei der Rechnung auf. Bewusst ohne
                            Preisangabe, damit sie auch ohne Finanzrecht dastehen darf. */}
                        {transferOriginLabel(service) && (
                            <p className="text-amber-300 text-[11px] mt-1">{transferOriginLabel(service)}</p>
                        )}
                    </div>
                    <button onClick={onClose} className="hover:bg-white/10 p-1 rounded transition-colors"><X className="w-5 h-5"/></button>
                </div>

                <div className="flex-1 overflow-y-auto p-6 space-y-6">

                    {/* STAND DER POSITION: ohne ihn bucht man blind — wie viel ist
                        schon erfasst, wie viel noch offen bis zum Soll. Nur Mengen,
                        keine Preise; der Dialog steht jedem offen. */}
                    <div className="p-3 rounded-xl border border-slate-200 bg-white shrink-0">
                        <div className="flex justify-between items-end gap-3">
                            <div>
                                <div className="text-[11px] uppercase font-bold text-slate-500">Bisher gebucht</div>
                                <div className="text-lg font-black text-slate-800">
                                    {formatQuantity(progress.used)}{' '}
                                    <span className="text-xs font-bold text-slate-400">{progress.hasTarget ? `von ${formatQuantity(progress.contracted)} ${service.unit}` : service.unit}</span>
                                </div>
                            </div>
                            {progress.hasTarget && (
                                <div className="text-right">
                                    <div className="text-[11px] uppercase font-bold text-slate-500">{progress.over > 0 ? 'Über Soll' : 'Offen'}</div>
                                    <div className={`text-lg font-black ${progress.over > 0 ? 'text-amber-600' : progress.remaining > 0 ? 'text-blue-700' : 'text-emerald-600'}`}>
                                        {formatQuantity(progress.over > 0 ? progress.over : progress.remaining)} <span className="text-xs font-bold text-slate-400">{service.unit}</span>
                                    </div>
                                </div>
                            )}
                        </div>
                        {progress.hasTarget && (
                            <div className="w-full bg-slate-100 rounded-full h-1.5 overflow-hidden mt-2">
                                <div className={`h-full rounded-full ${progress.over > 0 ? 'bg-amber-500' : 'bg-emerald-500'}`} style={{ width: `${progress.percent}%` }} />
                            </div>
                        )}
                        {progress.after && (
                            <p className={`text-[11px] font-bold mt-2 ${progress.hasTarget && progress.after.over > 0 ? 'text-amber-700' : 'text-slate-500'}`}>
                                Nach dieser Buchung: {formatQuantity(progress.after.used)}{progress.hasTarget ? ` von ${formatQuantity(progress.contracted)}` : ''} {service.unit}
                                {progress.hasTarget && (progress.after.over > 0
                                    ? ` · ${formatQuantity(progress.after.over)} über Soll`
                                    : ` · offen ${formatQuantity(progress.after.remaining)}`)}
                            </p>
                        )}
                    </div>

                    {/* PAKETPOSITION: kein Formular — Pakete bucht nur der
                        Bestandteile-Weg (Kernregel 7). Gebucht wird die Position,
                        sobald ihr letzter Bestandteil erledigt ist. */}
                    {istPaket ? (
                    <div className="p-4 rounded-xl border border-amber-200 bg-amber-50 space-y-3 shrink-0">
                        <div className="flex items-start gap-2">
                            <Info className="w-4 h-4 text-amber-600 shrink-0 mt-0.5" />
                            <p className="text-xs text-amber-900 leading-snug">
                                Diese Position hat Bestandteile{bestandteilStandKurz(service) ? ` (${bestandteilStandKurz(service)} erledigt)` : ''}. Sie wird nicht hier gebucht,
                                sondern über die Bestandteile: Ist der letzte Bestandteil erledigt, wird die Position automatisch gebucht.
                            </p>
                        </div>
                        {onOpenBestandteile && (
                            <Button onClick={onOpenBestandteile} className="w-full justify-center">
                                <ListChecks className="w-4 h-4 mr-2" />
                                Über Bestandteile buchen
                            </Button>
                        )}
                    </div>
                    ) : (
                    <div className={`p-4 rounded-xl border transition-all shrink-0 ${isHourly ? 'bg-blue-50 border-blue-200 ring-4 ring-blue-50' : 'bg-slate-50 border-slate-200'}`}>
                        <div className="flex justify-between items-center mb-4">
                            <label className={`text-sm font-bold ${isHourly ? 'text-blue-900' : 'text-slate-800'}`}>
                                {isFlatProof ? 'Nachweis erfassen' : isHourly ? 'Zeitaufwand erfassen' : 'Leistung erfassen'}
                            </label>
                            <span className={`text-[11px] px-2 py-0.5 rounded font-black uppercase tracking-widest ${isHourly ? 'bg-blue-600 text-white' : 'bg-slate-200 text-slate-600'}`}>
                                Einheit: {service.unit}
                            </span>
                        </div>
                        
                        <div className="grid grid-cols-2 gap-3 mb-4">
                             <div className="space-y-1">
                                <label className="text-[11px] uppercase font-bold text-slate-500 ml-1">Datum</label>
                                <input data-tutorial="leistung.datum" type="date" value={customDate} onChange={(e) => setCustomDate(e.target.value)} className="w-full px-3 py-1.5 border border-slate-300 rounded-lg text-sm outline-none focus:ring-2 focus:ring-blue-500 bg-white" />
                             </div>
                             <div className="space-y-1">
                                <label className="text-[11px] uppercase font-bold text-slate-500 ml-1">Uhrzeit</label>
                                <input type="time" value={customTime} onChange={(e) => setCustomTime(e.target.value)} className="w-full px-3 py-1.5 border border-slate-300 rounded-lg text-sm outline-none focus:ring-2 focus:ring-blue-500 bg-white" />
                             </div>
                        </div>

                        {showPerformer && (
                            <div className="space-y-1 mb-4">
                                <label className="text-[11px] uppercase font-bold text-slate-500 ml-1 flex items-center gap-1">
                                    <UserRound className="w-3 h-3" /> Erbracht durch
                                </label>
                                <select
                                    data-tutorial="leistung.erbrachtDurch"
                                    value={performerId}
                                    onChange={(e) => setChosenPerformerId(e.target.value)}
                                    className="w-full px-3 py-1.5 border border-slate-300 rounded-lg text-sm outline-none focus:ring-2 focus:ring-blue-500 bg-white"
                                >
                                    {auswahlEntfallen && <option value="">Bitte neu wählen …</option>}
                                    {performerOptions.map(o => (
                                        <option key={o.id} value={o.id}>{o.name}{o.hint ? ` (${o.hint})` : ''}</option>
                                    ))}
                                </select>
                                {auswahlEntfallen
                                    ? <p className="text-[11px] text-red-600 font-bold ml-1">Die gewählte Person ist nicht mehr wählbar (gesperrt). Bitte neu auswählen.</p>
                                    : <p className="text-[11px] text-slate-500 ml-1">Steht so im Protokoll und im Leistungsnachweis.</p>}
                            </div>
                        )}

                        <div className="space-y-4">
                            {isFlatProof ? (
                                <div className="rounded-xl border border-indigo-200 bg-indigo-50 p-3 flex items-start gap-2">
                                    <Info className="w-4 h-4 text-indigo-500 shrink-0 mt-0.5" />
                                    <p className="text-[11px] text-indigo-800 font-bold leading-snug">
                                        {flatRedirectTitle && <>Gebucht wird auf „{flatRedirectTitle}“. </>}
                                        {FLAT_PROOF_HINT}
                                    </p>
                                </div>
                            ) : (
                            <div className="space-y-1">
                                <label className={`text-[11px] uppercase font-bold block ml-1 ${isHourly ? 'text-blue-700' : 'text-slate-500'}`}>
                                    {isHourly ? 'Dauer in Stunden *' : 'Menge *'}
                                </label>
                                <div className="relative">
                                    {/*
                                      * type="text" + inputMode="decimal", NICHT type="number":
                                      * ein Zahlenfeld verwirft die Zwischenstufe "2." und
                                      * loescht damit still die halb getippte Eingabe. Der
                                      * Rohtext bleibt im State (siehe serviceUnits).
                                      */}
                                    <input
                                        data-tutorial="leistung.menge"
                                        type="text"
                                        inputMode="decimal"
                                        value={amountText}
                                        onChange={(e) => setAmountText(e.target.value)}
                                        className={`w-full p-4 border-2 rounded-xl text-center font-black text-2xl outline-none transition-all ${!isValid ? 'border-red-300 bg-red-50 text-red-700' : 'border-blue-300 bg-white text-blue-900 focus:ring-4 focus:ring-blue-100'}`}
                                        placeholder="0,00"
                                    />
                                    {isHourly && <div className="absolute right-4 top-1/2 -translate-y-1/2 font-bold text-slate-300 pointer-events-none">h</div>}
                                </div>
                                {/*
                                  * Die vereinbarte Vorgabe steht IMMER da, sobald die Position
                                  * eine traegt — unabhaengig vom Feldinhalt. Der Mitarbeiter
                                  * soll sehen, was erwartet wird, nicht erst nach einer
                                  * Fehleingabe. Wortgleich zum Abrechnungsdialog.
                                  */}
                                {minHours !== null && (
                                    <p className="text-[11px] text-blue-700 font-bold mt-2 flex items-center gap-1">
                                        <Info className="w-3 h-3" /> Mindestens {formatHoursLabel(minHours)} Std. ansetzen.
                                    </p>
                                )}
                                {belowMin && (
                                    <div className="mt-2 bg-amber-50 border border-amber-200 rounded-lg p-2.5 flex items-start gap-2">
                                        <AlertTriangle className="w-4 h-4 text-amber-500 shrink-0 mt-0.5" />
                                        <p className="text-[11px] text-amber-800 font-bold leading-snug">
                                            Weniger als die vereinbarten {formatHoursLabel(minHours as number)} Std. — buchen ist
                                            möglich, bitte nur bewusst unterschreiten.
                                        </p>
                                    </div>
                                )}
                                {!isValid && isHourly && (
                                    <p className="text-[11px] text-red-600 font-bold mt-2 flex items-center gap-1 animate-pulse">
                                        <AlertTriangle className="w-3 h-3" /> Eingabe erforderlich
                                    </p>
                                )}
                            </div>
                            )}

                            {isNep && (
                                <div data-tutorial="leistung.nepBegruendung" className="rounded-xl border border-amber-200 bg-amber-50 p-3 space-y-2">
                                    <div className="flex items-start gap-2">
                                        <AlertTriangle className="w-4 h-4 text-amber-500 shrink-0 mt-0.5" />
                                        <p className="text-[11px] text-amber-800 font-bold leading-snug">
                                            {NEP_BADGE}-Position: im Angebot nur mit Einheitspreis, nicht beauftragt.
                                            Buchen geht nur mit Begründung — sie steht im Protokoll und im Leistungsnachweis neben der Buchung.
                                        </p>
                                    </div>
                                    <div className="space-y-1">
                                        <label className="text-[11px] uppercase font-bold text-amber-800 block ml-1">Begründung *</label>
                                        <textarea
                                            rows={2}
                                            value={nepReason}
                                            onChange={(e) => setNepReason(e.target.value)}
                                            className={`w-full p-3 border rounded-xl text-sm outline-none focus:ring-2 focus:ring-amber-400 resize-none bg-white shadow-inner ${nepReasonMissing ? 'border-amber-400' : 'border-slate-300'}`}
                                            placeholder="z. B. Kunde hat am 12.09. nachträglich beauftragt (Herr Meier, per Mail)"
                                        />
                                        {nepReasonMissing && (
                                            <p className="text-[11px] text-amber-800 font-bold ml-1 flex items-center gap-1">
                                                <Info className="w-3 h-3" /> Ohne Begründung wird nicht gebucht.
                                            </p>
                                        )}
                                    </div>
                                </div>
                            )}

                            <div className="space-y-1">
                                <div className="flex justify-between items-center ml-1">
                                    <label className="text-[11px] uppercase font-bold text-slate-500 block">Beschreibung</label>
                                    <button onClick={() => setShowLargeInput(true)} className="text-blue-600 hover:text-blue-100 p-0.5 rounded transition-colors"><Maximize2 className="w-3 h-3" /></button>
                                </div>
                                <textarea 
                                    data-tutorial="leistung.bericht"
                                    rows={2}
                                    value={description}
                                    onChange={(e) => setDescription(e.target.value)}
                                    className="w-full p-3 border border-slate-300 rounded-xl text-sm outline-none focus:ring-2 focus:ring-blue-500 resize-none bg-white shadow-inner"
                                    placeholder="Tätigkeitsbericht..."
                                />
                            </div>
                        </div>
                        
                        <Button 
                            data-tutorial="leistung.speichern"
                            onClick={handleSubmit}
                            disabled={!canSave}
                            className={`w-full justify-center mt-4 py-3 shadow-lg transition-all ${canSave ? 'bg-blue-600 hover:bg-blue-700' : 'bg-slate-300 cursor-not-allowed'}`}
                        >
                            <CheckCircle2 className="w-4 h-4 mr-2" />
                            Eintrag speichern
                        </Button>
                    </div>
                    )}

                    <div>
                        <div className="flex justify-between items-center mb-3">
                            <h4 className="text-xs font-bold text-slate-500 uppercase flex items-center gap-2">
                                <History className="w-4 h-4" /> Bisherige Erfassung
                            </h4>
                        </div>
                        
                        <div className="bg-slate-50 border border-slate-200 rounded-xl p-2 max-h-48 overflow-y-auto custom-scrollbar">
                            {sortedLogs.length === 0 ? (
                                <div className="text-center py-8 text-slate-400 italic text-xs">Noch keine Einträge vorhanden.</div>
                            ) : (
                                sortedLogs.map(log => (
                                    <div key={log.id} className="bg-white border border-slate-100 rounded-lg p-2.5 shadow-sm mb-2 last:mb-0">
                                        <div className="flex justify-between items-center">
                                            <span className="text-[11px] font-bold text-slate-600">{formatDate(log.date)}</span>
                                            {/* Eine Nachweiszeile traegt Menge 0 — „0 Monat" laese sich wie ein Fehler. */}
                                            <span className="font-mono font-bold text-blue-600 text-xs">{isFlatProofEntry(log) ? 'Nachweis' : `${log.quantity} ${service.unit}`}</span>
                                        </div>
                                        {log.description && <p className="text-[11px] text-slate-500 mt-1 italic line-clamp-1">{log.description}</p>}
                                        {nepReasonLabel(log.nepReason) && <p className="text-[11px] text-amber-700 mt-0.5 font-medium line-clamp-1" title={nepReasonLabel(log.nepReason) ?? undefined}>{nepReasonLabel(log.nepReason)}</p>}
                                    </div>
                                ))
                            )}
                        </div>
                    </div>
                </div>

                {showLargeInput && (
                    <div className="absolute inset-0 z-50 bg-white flex flex-col animate-in fade-in zoom-in-95 duration-200">
                        <div className="bg-slate-900 text-white p-4 flex justify-between items-center shrink-0">
                             <h3 className="font-bold flex items-center gap-2"><FileText className="w-4 h-4"/> Beschreibung bearbeiten</h3>
                             <button onClick={() => setShowLargeInput(false)} className="hover:bg-white/10 p-1 rounded"><X className="w-5 h-5"/></button>
                        </div>
                        <div className="flex-1 p-4 bg-slate-50">
                            <textarea
                                value={description}
                                onChange={(e) => setDescription(e.target.value)}
                                className="w-full h-full p-4 border border-slate-200 rounded-xl text-base focus:ring-2 focus:ring-blue-500 outline-none resize-none leading-relaxed shadow-sm"
                                placeholder="Detaillierte Beschreibung eingeben..."
                                autoFocus
                            />
                        </div>
                        <div className="p-4 border-t border-slate-200 bg-white flex justify-end">
                            <Button onClick={() => setShowLargeInput(false)} className="w-full justify-center bg-blue-600 hover:bg-blue-700">Übernehmen</Button>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};
